import type { AuthTokens, SaProfile, TenantProfile } from '@order-pilot/shared';

/**
 * Session storage policy (see docs/architecture.md §5):
 *  - access token lives in memory only, so it dies with the tab;
 *  - refresh token is persisted so a reload does not sign the operator out;
 *  - the profile is cached alongside it purely to render the shell before
 *    the first API call resolves.
 *
 * This app hosts TWO portals with two JWT audiences (`aud: sa` for /admin,
 * `aud: tenant` for /portal). They get two INDEPENDENT stores: separate
 * localStorage keys, separate in-memory access tokens, separate listeners.
 * Signing into one never clobbers, reads or refreshes the other, so a tenant
 * token can never be presented on an /admin call and vice-versa.
 */
export type SessionKind = 'sa' | 'tenant';

/** The slice of a session the HTTP transport needs — profile-type agnostic. */
export interface TokenStore {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  /** Called after a successful token refresh (rotation). */
  setTokens(tokens: AuthTokens): void;
  clear(): void;
}

export interface SessionStore<TUser> extends TokenStore {
  /** Which portal this session belongs to. */
  readonly kind: SessionKind;
  subscribe(listener: () => void): () => void;
  getUser(): TUser | null;
  /** True while a refresh token — or an ephemeral access token — is held. */
  isAuthenticated(): boolean;
  /** Called after a successful login. */
  start(tokens: AuthTokens, user: TUser): void;
  /**
   * Starts a session from an access token ALONE: no refresh token, nothing
   * written to storage.
   *
   * This is what SA impersonation installs (docs/architecture.md §5.1). The
   * server deliberately issues no refresh token, so the session cannot be
   * renewed — and keeping it out of localStorage means it also cannot outlive
   * the tab. Any real session for this portal is dropped first: with a stored
   * refresh token still present, the first 401 would silently rotate the
   * impersonated view into somebody's own tenant session.
   */
  startEphemeral(accessToken: string, user: TUser): void;
  /** True while the session is the access-token-only kind above. */
  isEphemeral(): boolean;
}

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private-mode / disabled storage: fall back to an in-memory-only session.
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore — storage is a convenience, not a requirement */
  }
}

/** Reads a field only when it really is a string, so a mangled cache is discarded. */
function str(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' ? value : null;
}

/**
 * Like `str`, but for a field whose real, valid value is `string | null` (a
 * tenant with no logo caches as `null`, not "missing"). Returns `undefined` —
 * distinct from a legitimate `null` — only when the cached value is neither,
 * so the caller can still tell a mangled cache from "no logo".
 */
function nullableStr(source: Record<string, unknown>, key: string): string | null | undefined {
  const value = source[key];
  if (value === null) return null;
  return typeof value === 'string' ? value : undefined;
}

type UserParser<TUser> = (value: Record<string, unknown>) => TUser | null;

function parseCachedUser<TUser>(raw: string | null, parse: UserParser<TUser>): TUser | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (value === null || typeof value !== 'object') return null;
    return parse(value as Record<string, unknown>);
  } catch {
    return null;
  }
}

function createSessionStore<TUser>(kind: SessionKind, parse: UserParser<TUser>): SessionStore<TUser> {
  const refreshTokenKey = `order-pilot.${kind}.refreshToken`;
  const userKey = `order-pilot.${kind}.user`;

  let accessToken: string | null = null;
  let cachedUser: TUser | null | undefined;
  /** Access-token-only session (impersonation): never persisted, never renewed. */
  let ephemeral = false;
  const listeners = new Set<() => void>();

  function emit(): void {
    for (const listener of listeners) listener();
  }

  return {
    kind,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getAccessToken() {
      return accessToken;
    },

    getRefreshToken() {
      return readStored(refreshTokenKey);
    },

    getUser() {
      if (cachedUser === undefined) cachedUser = parseCachedUser(readStored(userKey), parse);
      return cachedUser;
    },

    isAuthenticated() {
      if (readStored(refreshTokenKey) !== null) return true;
      // An ephemeral session is "signed in" for exactly as long as the access
      // token it was handed: there is nothing to reload it from.
      return ephemeral && accessToken !== null;
    },

    isEphemeral() {
      return ephemeral;
    },

    start(tokens, user) {
      ephemeral = false;
      accessToken = tokens.accessToken;
      writeStored(refreshTokenKey, tokens.refreshToken);
      cachedUser = user;
      writeStored(userKey, JSON.stringify(user));
      emit();
    },

    startEphemeral(token, user) {
      ephemeral = true;
      accessToken = token;
      cachedUser = user;
      // Nothing is persisted, and any real session for this portal is ended
      // locally — see the interface for why holding both is unsafe.
      writeStored(refreshTokenKey, null);
      writeStored(userKey, null);
      emit();
    },

    setTokens(tokens) {
      // A successful rotation means a real, renewable session again.
      ephemeral = false;
      accessToken = tokens.accessToken;
      writeStored(refreshTokenKey, tokens.refreshToken);
      emit();
    },

    clear() {
      ephemeral = false;
      accessToken = null;
      cachedUser = null;
      writeStored(refreshTokenKey, null);
      writeStored(userKey, null);
      emit();
    },
  };
}

/** Super-admin session — `aud: sa` tokens, used only by /admin/* calls. */
export const saSession: SessionStore<SaProfile> = createSessionStore<SaProfile>('sa', (value) => {
  const id = str(value, 'id');
  const email = str(value, 'email');
  const username = str(value, 'username');
  if (id === null || email === null || username === null) return null;
  // A profile stored before roles existed has none — every login was a full
  // console user then, which is also what the API assumes for such a token.
  const role = str(value, 'role') === 'ISSUER' ? 'ISSUER' : 'OWNER';
  return { id, email, username, role };
});

/** Tenant-admin session — `aud: tenant` tokens, used only by /portal/* calls. */
export const tenantSession: SessionStore<TenantProfile> = createSessionStore<TenantProfile>(
  'tenant',
  (value) => {
    const id = str(value, 'id');
    const tenantId = str(value, 'tenantId');
    const tenantName = str(value, 'tenantName');
    const tenantLogoUrl = nullableStr(value, 'tenantLogoUrl');
    const email = str(value, 'email');
    const username = str(value, 'username');
    const timezone = str(value, 'timezone');
    const currency = str(value, 'currency');
    if (
      id === null ||
      tenantId === null ||
      tenantName === null ||
      tenantLogoUrl === undefined ||
      email === null ||
      username === null ||
      timezone === null ||
      currency === null
    ) {
      return null;
    }
    const impersonated = value.impersonated;
    return {
      id,
      tenantId,
      tenantName,
      tenantLogoUrl,
      email,
      username,
      timezone,
      currency,
      smartMenuEnabled: value.smartMenuEnabled === true,
      ...(typeof impersonated === 'boolean' ? { impersonated } : {}),
    };
  },
);
